import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined'
import { Box, Typography } from '@mui/material'
import { useMemo, useState } from 'react'
import { toast } from 'react-toastify'

import { Loader } from '@/components'
import { ReleaseDto } from '@/endpoints/models'
import { useAppContext, useAppStore } from '@/hooks'
import { BranchStatCard } from '@/pages/User/pages/Branches/components/BranchDetailUi'
import {
	useChangeReleaseStatusMutation,
	useGetReleaseEnvironmentsQuery,
	useGetReleaseStructuresQuery,
	useGetSystemReleaseQuery,
	useUpdateDeploymentPackageMutation,
	useUpdateSystemReleaseMutation,
} from '@/rtkApi'
import { StructureDto } from '@/rtkApi/mddApiSliceGenerated'
import { ReleaseStatusEnum } from '@/rtkApi/mddApiSliceGeneratedV2'
import { catalogCanvasSx } from '@/styles/catalogUi'
import { hasStructureEditPermission } from '@/utils'
import { getApiErrorDescription, logErrorAndNotifyWithToast } from '@/utils/errorHandling'

import { PackageElements } from './detail/PackageElements'
import { PackageEnvironments } from './detail/PackageEnvironments'
import { PackageHeader } from './detail/PackageHeader'
import { PackageInfoSection } from './detail/PackageInfoSection'
import { mapDeploymentPackage } from './mapDeploymentPackage'
import { DEPLOYMENT_PACKAGE_PAGE_SIZE } from './types'

type PackageDetailProps = {
	onBack: () => void
	packageId: string
	systemId: number
}

const fallbackSystemNode = (systemId: number): StructureDto => ({
	canEdit: false,
	code: '',
	companyId: 0,
	hasChildren: false,
	id: systemId,
	name: '',
	type: 'SYSTEM',
})

export const PackageDetail = ({ onBack, packageId, systemId }: PackageDetailProps) => {
	const { t } = useAppContext()
	const storedNode = useAppStore((state) => state.node.nodes[systemId])
	const systemNode = storedNode ?? fallbackSystemNode(systemId)
	const dpId = Number(packageId)
	const skip = Number.isNaN(dpId)

	const release = useGetSystemReleaseQuery({ dpId }, { skip })
	const environments = useGetReleaseEnvironmentsQuery({ dpId }, { skip })
	const structures = useGetReleaseStructuresQuery(
		{ dpId, page: 1, pageSize: DEPLOYMENT_PACKAGE_PAGE_SIZE },
		{ skip },
	)
	const [updateRelease, { isLoading: isSaving }] = useUpdateSystemReleaseMutation()
	const [changeStatus] = useChangeReleaseStatusMutation()
	const [syncPackage] = useUpdateDeploymentPackageMutation()
	const [syncing, setSyncing] = useState(false)

	const envList = useMemo(() => environments.data ?? [], [environments.data])
	const elementList = useMemo(() => structures.data?.content ?? [], [structures.data])

	const refetchAll = () => {
		void release.refetch()
		void environments.refetch()
		void structures.refetch()
	}

	const notifyFailure = (operation: string, error: unknown) =>
		logErrorAndNotifyWithToast({
			context: { operation },
			detail: getApiErrorDescription(error),
			error,
			messageKey: 'DEPLOYMENT_PACKAGES_UPDATE_FAILED',
			t,
		})

	if (skip || release.isLoading) {
		return <Loader loaded={false} $absolute />
	}

	if (release.isError || !release.data) {
		return (
			<Box sx={{ ...catalogCanvasSx, px: 4.5, pt: 2.5 }} data-testid="deployment-package-missing">
				<BackLink label={t('DEPLOYMENT_PACKAGES_BACK')} onBack={onBack} />
				<Typography sx={{ color: '#A8B4B8', fontSize: 13 }}>{t('RELEASE_NOT_FOUND')}</Typography>
			</Box>
		)
	}

	const data = release.data
	const pkg = mapDeploymentPackage(data)
	const canEdit = data.canEditWholeContent ?? hasStructureEditPermission(systemNode)
	const legacyPackage: ReleaseDto = {
		description: data.description,
		id: data.id,
		name: data.name,
		status: data.status,
	}
	const releasedCount = envList.filter((env) => env.released).length

	const saveInfo = async (values: { description: string; name: string }) => {
		try {
			await updateRelease({
				dpId,
				releaseDto: {
					branch: { id: data.branch.id },
					responsibleUser: data.responsibleUser
						? { id: data.responsibleUser.id }
						: undefined,
					status: data.status,
					...values,
				},
			}).unwrap()
		} catch (error) {
			notifyFailure('PackageDetail.saveInfo', error)
		}
	}

	const syncWithBranch = async () => {
		setSyncing(true)
		try {
			await syncPackage({ branchId: data.branch.id }).unwrap()
			toast(t('BRANCHES_UPDATE_DEPLOYMENT_PACKAGE_SUCCESS'), { type: 'success' })
			refetchAll()
		} catch (error) {
			logErrorAndNotifyWithToast({
				context: { operation: 'PackageDetail.syncWithBranch' },
				detail: getApiErrorDescription(error),
				error,
				messageKey: 'BRANCHES_UPDATE_DEPLOYMENT_PACKAGE_FAILED',
				t,
			})
		} finally {
			setSyncing(false)
		}
	}

	const saveStatus = async (newStatus: ReleaseStatusEnum) => {
		try {
			await changeStatus({ dpId, newStatus }).unwrap()
			void release.refetch()
		} catch (error) {
			notifyFailure('PackageDetail.saveStatus', error)
		}
	}

	return (
		<Box sx={{ ...catalogCanvasSx, flex: 1 }} data-testid="deployment-package-detail">
			<Box sx={{ pb: 6, pt: 2.5, px: 4.5 }}>
				<BackLink label={t('DEPLOYMENT_PACKAGES_BACK')} onBack={onBack} />
				<PackageHeader
					canEdit={canEdit}
					pkg={pkg}
					syncing={syncing}
					systemName={systemNode.name}
					onChangeStatus={(status) => void saveStatus(status)}
					onSync={() => void syncWithBranch()}
				/>

				<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25, mt: 2 }}>
					<BranchStatCard label={t('ELEMENTS_IN_RELEASE')} value={String(elementList.length)} />
					<BranchStatCard label={t('ENVIRONMENTS_IN_RELEASE')} value={String(envList.length)} />
					<BranchStatCard label={t('RELEASED')} value={`${releasedCount}/${envList.length}`} />
				</Box>

				<PackageInfoSection
					canEdit={canEdit}
					isSaving={isSaving}
					pkg={pkg}
					onSave={(values) => void saveInfo(values)}
				/>
				<PackageEnvironments
					canEdit={canEdit}
					environments={envList}
					legacyPackage={legacyPackage}
					systemNode={systemNode}
					onChanged={refetchAll}
				/>
				<PackageElements
					canEdit={canEdit}
					dpId={dpId}
					elements={elementList}
					environments={envList}
					onChanged={refetchAll}
				/>
			</Box>
		</Box>
	)
}

const BackLink = ({ label, onBack }: { label: string; onBack: () => void }) => (
	<Box
		onClick={onBack}
		data-testid="deployment-package-back"
		sx={{
			alignItems: 'center',
			color: '#6A6A6A',
			cursor: 'pointer',
			display: 'inline-flex',
			fontSize: 11.5,
			fontWeight: 600,
			gap: '5px',
			mb: 1.5,
			'&:hover': { color: '#1F4954' },
		}}
	>
		<ArrowBackOutlinedIcon sx={{ fontSize: 13 }} />
		{label}
	</Box>
)
